import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { fetchMemoryBanks, fetchBankStats, fetchMemories, deleteDocument, clearBankMemories } from '@api/backoffice';
import ExportButton from '@components/Backoffice/ExportButton';
import { downloadBackofficeCsv } from '@utils/csvExport';
import useSnackbar from '@hooks/useSnackbar';
import {
  PageTitle,
  Card,
  CardHeader,
  CardBody,
  Table,
  SearchBar,
  Badge,
  PaginationBar,
  PaginationInfo,
  PaginationButton,
  PaginationActions,
  EmptyState,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  TabBar,
  Tab,
} from '@components/Backoffice/BackofficeLayout.styled';
import {
  BankGrid,
  BankCard,
  BankName,
  BankId,
  BankMission,
  StatRow,
  StatItem,
  MemoryContent,
  TypeFilter,
  FilterChip,
  BankDetailHeader,
  BackButton,
  HeaderControls,
  MissionBlock,
  MissionText,
  DeleteButton,
  InlineConfirmButton,
  DangerButton,
  ConfirmOverlay,
  ConfirmDialog,
  ConfirmTitle,
  ConfirmBody,
  ConfirmActions,
  GraphButton,
  ReflectButton,
} from './BackofficeMemory.styled';
import type {
  HindsightBankSummary,
  HindsightBankStats,
  HindsightMemoryItem,
  HindsightMemoryPage,
} from './types';

const MemoryGraphModal = lazy(() => import('./MemoryGraphModal'));
const ReflectModal = lazy(() => import('./ReflectModal'));

const PAGE_SIZE = 50;
const MEMORY_TYPES = ['all', 'world', 'experience', 'observation'] as const;

type MemoryTypeFilter = (typeof MEMORY_TYPES)[number];

const typeVariant = (type: string) => {
  if (type === 'world') return 'info' as const;
  if (type === 'experience') return 'success' as const;
  if (type === 'observation') return 'warning' as const;
  return 'info' as const;
};

const BackofficeMemory: React.FC = () => {
  const { showError, showSuccess } = useSnackbar();
  const [tab, setTab] = useState<'banks' | 'memories'>('banks');
  const [banks, setBanks] = useState<HindsightBankSummary[]>([]);
  const [bankStats, setBankStats] = useState<Record<string, HindsightBankStats>>({});
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(false);

  const [selectedBank, setSelectedBank] = useState<string | null>(null);
  const [memoryData, setMemoryData] = useState<HindsightMemoryPage | null>(null);
  const [memoryLoading, setMemoryLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [typeFilter, setTypeFilter] = useState<MemoryTypeFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const loadBanks = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchMemoryBanks();
      setBanks(result.banks);
      setEnabled(result.enabled);

      for (const bank of result.banks) {
        try {
          const stats = await fetchBankStats(bank.bankId);
          setBankStats((prev) => ({ ...prev, [bank.bankId]: stats }));
        } catch {
          // stats are best-effort
        }
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to load memory banks');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const loadMemories = useCallback(async () => {
    if (!selectedBank) return;
    setMemoryLoading(true);
    try {
      const result = await fetchMemories(selectedBank, {
        type: typeFilter === 'all' ? undefined : typeFilter,
        q: searchQuery || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setMemoryData(result);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to load memories');
    } finally {
      setMemoryLoading(false);
    }
  }, [selectedBank, typeFilter, searchQuery, offset, showError]);

  useEffect(() => {
    loadBanks();
  }, [loadBanks]);

  useEffect(() => {
    if (selectedBank) {
      loadMemories();
    }
  }, [selectedBank, loadMemories]);

  const handleBankSelect = (bankId: string) => {
    setSelectedBank(bankId);
    setOffset(0);
    setTypeFilter('all');
    setSearchQuery('');
    setMemoryData(null);
    setTab('memories');
  };

  const handleBackToBanks = () => {
    setSelectedBank(null);
    setMemoryData(null);
    setTab('banks');
  };

  const handleTypeChange = (type: MemoryTypeFilter) => {
    setTypeFilter(type);
    setOffset(0);
  };

  const handleDeleteDocument = useCallback(
    async (documentId: string) => {
      if (!selectedBank) return;
      try {
        await deleteDocument(selectedBank, documentId);
        showSuccess('Conversation memories deleted — they will be re-extracted on the next message');
        await loadMemories();
        const stats = await fetchBankStats(selectedBank);
        setBankStats((prev) => ({ ...prev, [selectedBank]: stats }));
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to delete conversation memories');
      }
    },
    [selectedBank, loadMemories, showSuccess, showError],
  );

  const handleClearBank = useCallback(async () => {
    if (!selectedBank) return;
    try {
      await clearBankMemories(selectedBank);
      showSuccess('All memories deleted');
      await loadMemories();
      const stats = await fetchBankStats(selectedBank);
      setBankStats((prev) => ({ ...prev, [selectedBank]: stats }));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to clear bank');
    }
  }, [selectedBank, loadMemories, showSuccess, showError]);

  if (!enabled) {
    return (
      <>
        <PageTitle>Memory (Hindsight)</PageTitle>
        <Card>
          <EmptyState>
            Hindsight memory service is not configured. Set HINDSIGHT_BASE_URL to enable.
          </EmptyState>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageTitle>Memory (Hindsight)</PageTitle>

      {selectedBank ? (
        <MemoryBrowser
          bankId={selectedBank}
          bank={banks.find((b) => b.bankId === selectedBank) ?? null}
          stats={bankStats[selectedBank] ?? null}
          memoryData={memoryData}
          loading={memoryLoading}
          offset={offset}
          typeFilter={typeFilter}
          searchQuery={searchQuery}
          onBack={handleBackToBanks}
          onOffsetChange={setOffset}
          onTypeChange={handleTypeChange}
          onSearchChange={(q) => {
            setSearchQuery(q);
            setOffset(0);
          }}
          onDeleteDocument={handleDeleteDocument}
          onClearBank={handleClearBank}
        />
      ) : (
        <>
          <TabBar>
            <Tab $active={tab === 'banks'} onClick={() => setTab('banks')}>
              Banks
            </Tab>
          </TabBar>

          <BankListContent
            loading={loading}
            banks={banks}
            bankStats={bankStats}
            onBankSelect={handleBankSelect}
          />
        </>
      )}
    </>
  );
};

function BankListContent({
  loading,
  banks,
  bankStats,
  onBankSelect,
}: Readonly<{
  loading: boolean;
  banks: HindsightBankSummary[];
  bankStats: Record<string, HindsightBankStats>;
  onBankSelect: (bankId: string) => void;
}>) {
  if (loading) {
    return <EmptyState>Loading banks...</EmptyState>;
  }

  if (banks.length === 0) {
    return (
      <Card>
        <EmptyState>No memory banks found</EmptyState>
      </Card>
    );
  }

  return (
    <BankGrid>
      {banks.map((bank) => {
        const stats = bankStats[bank.bankId];
        return (
          <BankCard key={bank.bankId} onClick={() => onBankSelect(bank.bankId)}>
            <BankName>{bank.name || bank.bankId}</BankName>
            <BankId>{bank.bankId}</BankId>
            {bank.mission && <BankMission>{bank.mission}</BankMission>}
            {stats && (
              <StatRow>
                <StatItem>{stats.totalNodes} memories</StatItem>
                <StatItem>{stats.totalDocuments} docs</StatItem>
              </StatRow>
            )}
          </BankCard>
        );
      })}
    </BankGrid>
  );
}

interface MemoryBrowserProps {
  readonly bankId: string;
  readonly bank: HindsightBankSummary | null;
  readonly stats: HindsightBankStats | null;
  readonly memoryData: HindsightMemoryPage | null;
  readonly loading: boolean;
  readonly offset: number;
  readonly typeFilter: MemoryTypeFilter;
  readonly searchQuery: string;
  readonly onBack: () => void;
  readonly onOffsetChange: (offset: number) => void;
  readonly onTypeChange: (type: MemoryTypeFilter) => void;
  readonly onSearchChange: (q: string) => void;
  readonly onDeleteDocument: (documentId: string) => Promise<void>;
  readonly onClearBank: () => Promise<void>;
}

function MemoryBrowser({
  bankId,
  bank,
  stats,
  memoryData,
  loading,
  offset,
  typeFilter,
  searchQuery,
  onBack,
  onOffsetChange,
  onTypeChange,
  onSearchChange,
  onDeleteDocument,
  onClearBank,
}: MemoryBrowserProps) {
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showGraph, setShowGraph] = useState(false);
  const [showReflect, setShowReflect] = useState(false);
  const totalPages = memoryData ? Math.ceil(memoryData.total / PAGE_SIZE) : 0;
  const currentPage = Math.floor(offset / PAGE_SIZE);

  return (
    <>
      <BankDetailHeader>
        <BackButton onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </BackButton>
        <BankName as="h1">{bank?.name || bankId}</BankName>
        <GraphButton onClick={() => setShowGraph(true)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="6" cy="6" r="2.5" />
            <circle cx="18" cy="6" r="2.5" />
            <circle cx="18" cy="18" r="2.5" />
            <circle cx="6" cy="18" r="2.5" />
            <path d="M8.5 6h7M18 8.5v7M8.5 18h7M6 8.5v7" stroke="currentColor" strokeWidth="1.5" fill="none" />
            <path d="M8 8l8 8" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </svg>
          View Graph
        </GraphButton>
        <ReflectButton onClick={() => setShowReflect(true)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" />
          </svg>
          Reflect
        </ReflectButton>
        <DangerButton onClick={() => setShowClearConfirm(true)}>Empty Bank</DangerButton>
      </BankDetailHeader>

      {showGraph && (
        <Suspense fallback={null}>
          <MemoryGraphModal
            bankId={bankId}
            bankName={bank?.name || bankId}
            onClose={() => setShowGraph(false)}
          />
        </Suspense>
      )}

      {showReflect && (
        <Suspense fallback={null}>
          <ReflectModal
            bankId={bankId}
            bankName={bank?.name || bankId}
            onClose={() => setShowReflect(false)}
          />
        </Suspense>
      )}

      {showClearConfirm && (
        <ConfirmOverlay onClick={() => setShowClearConfirm(false)}>
          <ConfirmDialog onClick={(e) => e.stopPropagation()}>
            <ConfirmTitle>Empty Bank</ConfirmTitle>
            <ConfirmBody>
              This will permanently delete all memories in this bank. This action cannot be undone.
            </ConfirmBody>
            <ConfirmActions>
              <InlineConfirmButton onClick={() => setShowClearConfirm(false)}>
                Cancel
              </InlineConfirmButton>
              <InlineConfirmButton
                $danger
                onClick={() => {
                  setShowClearConfirm(false);
                  onClearBank();
                }}
              >
                Delete All Memories
              </InlineConfirmButton>
            </ConfirmActions>
          </ConfirmDialog>
        </ConfirmOverlay>
      )}

      {(bank || stats) && (
        <Card>
          <CardBody>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Bank ID</DetailLabel>
                <DetailValue>{bankId}</DetailValue>
              </DetailItem>
              {stats && (
                <>
                  <DetailItem>
                    <DetailLabel>Total Memories</DetailLabel>
                    <DetailValue>{stats.totalNodes}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Documents</DetailLabel>
                    <DetailValue>{stats.totalDocuments}</DetailValue>
                  </DetailItem>
                  <DetailItem>
                    <DetailLabel>Links</DetailLabel>
                    <DetailValue>{stats.totalLinks}</DetailValue>
                  </DetailItem>
                </>
              )}
              {stats?.nodesByFactType &&
                Object.entries(stats.nodesByFactType).map(([type, count]) => (
                  <DetailItem key={type}>
                    <DetailLabel>{type}</DetailLabel>
                    <DetailValue>{count}</DetailValue>
                  </DetailItem>
                ))}
            </DetailGrid>
            {bank?.mission && (
              <MissionBlock>
                <DetailItem>
                  <DetailLabel>Mission</DetailLabel>
                  <MissionText>{bank.mission}</MissionText>
                </DetailItem>
              </MissionBlock>
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader>
          <HeaderControls>
            <SearchBar
              placeholder="Search memories..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            <ExportButton
              onExport={() =>
                downloadBackofficeCsv(
                  `/api/v1/backoffice/memory/banks/${bankId}/memories/export`,
                  {
                    type: typeFilter === 'all' ? '' : typeFilter,
                    q: searchQuery || '',
                  },
                  `memories_${bankId}_${new Date().toISOString().slice(0, 10)}.csv`
                )
              }
            />
            <TypeFilter>
              {MEMORY_TYPES.map((t) => (
                <FilterChip key={t} $active={typeFilter === t} onClick={() => onTypeChange(t)}>
                  {t}
                </FilterChip>
              ))}
            </TypeFilter>
          </HeaderControls>
        </CardHeader>

        <MemoryTableContent
          loading={loading}
          memoryData={memoryData}
          offset={offset}
          totalPages={totalPages}
          currentPage={currentPage}
          onOffsetChange={onOffsetChange}
          onDeleteDocument={onDeleteDocument}
        />
      </Card>
    </>
  );
}

function MemoryTableContent({
  loading,
  memoryData,
  offset,
  totalPages,
  currentPage,
  onOffsetChange,
  onDeleteDocument,
}: Readonly<{
  loading: boolean;
  memoryData: HindsightMemoryPage | null;
  offset: number;
  totalPages: number;
  currentPage: number;
  onOffsetChange: (offset: number) => void;
  onDeleteDocument: (documentId: string) => Promise<void>;
}>) {
  const [confirmingDocId, setConfirmingDocId] = useState<string | null>(null);

  if (loading && !memoryData) {
    return <EmptyState>Loading memories...</EmptyState>;
  }

  if (memoryData?.items.length === 0) {
    return <EmptyState>No memories found</EmptyState>;
  }

  return (
    <>
      {confirmingDocId && (
        <ConfirmOverlay onClick={() => setConfirmingDocId(null)}>
          <ConfirmDialog onClick={(e) => e.stopPropagation()}>
            <ConfirmTitle>Forget Conversation</ConfirmTitle>
            <ConfirmBody>
              This will delete <strong>all memories</strong> extracted from this
              conversation (document {confirmingDocId.slice(0, 8)}…). They will
              be re-extracted automatically on the next message in that
              conversation.
            </ConfirmBody>
            <ConfirmActions>
              <InlineConfirmButton onClick={() => setConfirmingDocId(null)}>
                Cancel
              </InlineConfirmButton>
              <InlineConfirmButton
                $danger
                onClick={() => {
                  const docId = confirmingDocId;
                  setConfirmingDocId(null);
                  onDeleteDocument(docId);
                }}
              >
                Delete All
              </InlineConfirmButton>
            </ConfirmActions>
          </ConfirmDialog>
        </ConfirmOverlay>
      )}

      <Table>
        <thead>
          <tr>
            <th>Type</th>
            <th>Memory</th>
            <th>Conversation</th>
            <th>Tags</th>
            <th>Entities</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {(memoryData?.items ?? []).map((memory: HindsightMemoryItem) => (
            <tr key={memory.id}>
              <td>
                {memory.type ? (
                  <Badge $variant={typeVariant(memory.type)}>{memory.type}</Badge>
                ) : (
                  '\u2014'
                )}
              </td>
              <MemoryContent>{memory.text}</MemoryContent>
              <td>{memory.conversationId ? memory.conversationId.slice(0, 8) : '\u2014'}</td>
              <td>{memory.tags || '\u2014'}</td>
              <td>{memory.entities || '\u2014'}</td>
              <td>{memory.date ? new Date(memory.date).toLocaleDateString() : '\u2014'}</td>
              <td>
                {memory.conversationId ? (
                  <DeleteButton
                    onClick={() => setConfirmingDocId(memory.conversationId)}
                    title="Forget conversation memories"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                    </svg>
                  </DeleteButton>
                ) : (
                  '\u2014'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {memoryData && totalPages > 1 && (
        <PaginationBar>
          <PaginationInfo>
            Page {currentPage + 1} of {totalPages} ({memoryData.total} total)
          </PaginationInfo>
          <PaginationActions>
            <PaginationButton
              $disabled={offset === 0}
              disabled={offset === 0}
              onClick={() => onOffsetChange(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </PaginationButton>
            <PaginationButton
              $disabled={currentPage >= totalPages - 1}
              disabled={currentPage >= totalPages - 1}
              onClick={() => onOffsetChange(offset + PAGE_SIZE)}
            >
              Next
            </PaginationButton>
          </PaginationActions>
        </PaginationBar>
      )}
    </>
  );
}

export default BackofficeMemory;
